import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';

const SUBTITLES = [
  'Votre document a bien été transmis !',
  'Vous allez recevoir une copie par mail.',
];

const CONFETTI_COLOR_TOKENS = [
  '--foreground',
  '--primary',
  '--primary-glow',
  '--accent',
  '--accent-glow',
  '--success',
  '--chart-1',
  '--chart-2',
  '--chart-3',
  '--chart-4',
  '--chart-5',
];

function resolveConfettiColors() {
  const styles = getComputedStyle(document.documentElement);

  const colors = CONFETTI_COLOR_TOKENS.map((token) => styles.getPropertyValue(token).trim())
    .filter(Boolean)
    .map((value) => `hsl(${value})`);

  if (colors.length > 0) {
    return colors;
  }

  return ['hsl(207 90% 54%)', 'hsl(190 95% 45%)', 'hsl(142 76% 46%)', 'hsl(262 83% 58%)', 'hsl(43 96% 56%)'];
}

function getSafeExternalUrl(rawUrl) {
  if (!rawUrl) return null;

  const candidate = rawUrl.trim();
  if (!candidate) return null;

  const withProtocol = /^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`;

  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export default function RemerciementSignaturePage() {
  const canvasRef = useRef(null);
  const [subtitleIndex, setSubtitleIndex] = useState(0);
  const [subtitleVisible, setSubtitleVisible] = useState(true);

  const { providerName, providerWebsite } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const providerNameFromQuery = params.get('nomPrestataire') || 'votre prestataire';
    const websiteFromQuery = params.get('sitePrestaitaire') || params.get('sitePrestataire');

    return {
      providerName: providerNameFromQuery,
      providerWebsite: getSafeExternalUrl(websiteFromQuery),
    };
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    const interval = window.setInterval(() => {
      setSubtitleVisible(false);
      window.setTimeout(() => {
        setSubtitleIndex((current) => (current + 1) % SUBTITLES.length);
        setSubtitleVisible(true);
      }, 280);
    }, 2600);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    if (!context) return undefined;

    let animationFrameId;
    let confettis = [];
    const confettiColors = resolveConfettiColors();

    const createConfetti = (width, height) => {
      const kindRandom = Math.random();
      const kind = kindRandom > 0.7 ? 'circle' : kindRandom > 0.4 ? 'strip' : 'rect';

      return {
      x: Math.random() * width,
      y: -30 - Math.random() * height,
      width: 6 + Math.random() * 10,
      height: 8 + Math.random() * 14,
      kind,
      color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
      velocityX: -2.4 + Math.random() * 4.8,
      velocityY: 1.8 + Math.random() * 4.2,
      gravity: 0.04 + Math.random() * 0.04,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: -0.22 + Math.random() * 0.44,
      swayOffset: Math.random() * Math.PI * 2,
      swaySpeed: 0.01 + Math.random() * 0.03,
      scale: 0.85 + Math.random() * 0.45,
    };
    };

    const initializeCanvas = () => {
      const devicePixelRatio = window.devicePixelRatio || 1;
      const width = window.innerWidth;
      const height = window.innerHeight;

      canvas.width = width * devicePixelRatio;
      canvas.height = height * devicePixelRatio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      confettis = Array.from({ length: 240 }, () => createConfetti(width, height));
    };

    const animate = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      context.clearRect(0, 0, width, height);

      confettis.forEach((confetti, index) => {
        confetti.swayOffset += confetti.swaySpeed;
        confetti.x += confetti.velocityX + Math.sin(confetti.swayOffset) * 0.6;
        confetti.y += confetti.velocityY;
        confetti.velocityY += confetti.gravity;
        confetti.rotation += confetti.rotationSpeed;

        context.save();
        context.translate(confetti.x, confetti.y);
        context.rotate(confetti.rotation);
        context.fillStyle = confetti.color;
        context.globalAlpha = 0.98;
        context.scale(confetti.scale, confetti.scale);

        if (confetti.kind === 'circle') {
          context.beginPath();
          context.arc(0, 0, confetti.width / 2, 0, Math.PI * 2);
          context.fill();
        } else if (confetti.kind === 'strip') {
          context.fillRect(-confetti.width / 4, -confetti.height / 2, confetti.width / 2, confetti.height);
        } else {
          context.fillRect(-confetti.width / 2, -confetti.height / 2, confetti.width, confetti.height);
        }

        context.shadowColor = confetti.color;
        context.shadowBlur = 6;
        context.restore();

        if (confetti.y > height + 30 || confetti.x < -40 || confetti.x > width + 40) {
          confettis[index] = createConfetti(width, height);
          confettis[index].y = -20;
        }
      });

      animationFrameId = window.requestAnimationFrame(animate);
    };

    initializeCanvas();
    animate();

    const handleResize = () => {
      initializeCanvas();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-background">
      <canvas ref={canvasRef} className="pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-3xl rounded-[2rem] border border-border/60 bg-card/75 p-8 text-center shadow-lg backdrop-blur-md md:p-12">
          <h1 className="text-6xl font-bold tracking-tight text-foreground md:text-8xl">MERCI !</h1>
          <p
            aria-live="polite"
            className={`mt-6 text-xl font-medium text-primary transition-all duration-300 md:text-2xl ${
              subtitleVisible ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
            }`}
          >
            {SUBTITLES[subtitleIndex]}
          </p>
          <div className="mt-10 flex justify-center">
            <Button
              variant="hero"
              size="lg"
              onClick={() => {
                if (providerWebsite) {
                  window.location.assign(providerWebsite);
                }
              }}
              disabled={!providerWebsite}
            >
              {`Retourner sur le site de ${providerName}`}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
